/**
 * QueueKanbanBoard - Container principal do Kanban da Fila de Atendimento
 *
 * CHANGELOG:
 *   v3.3.0 - 2026-06-26 — Removida a chamada `onRefresh()` ao fechar
 *            o modal de chat. O Realtime unificado já mantém os cards
 *            atualizados via patch otimista; invalidar tudo ao fechar
 *            mascarava o realtime e gerava RPCs desnecessárias.
 *   v3.2.0 - 2026-06-26 — Monta useServiceQueueRealtime uma única vez
 *            (1 canal por aba) substituindo as 2 subscriptions × 5
 *            colunas (10 canais) que existiam antes. Patch otimista
 *            com reorder-ao-topo + invalidação debounced.
 *   v3.0.0 — Arquitetura de paginação por coluna.
 */

import { useState, useMemo, useCallback } from 'react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { QueueKanbanColumn, QUEUE_COLUMNS } from './QueueKanbanColumn';
import { SentryErrorBoundary } from '@/lib/sentry';
import { ErrorFallback } from '@/components/ErrorFallback';
import { useServiceQueueRealtime } from '@/hooks/useServiceQueueRealtime';
import type { QueueItem, QueueStats, QueueFilters } from '@/hooks/useServiceQueue';

interface QueueKanbanBoardProps {
  /** Filtros globais aplicados a todas as colunas */
  filters?: Partial<QueueFilters>;
  /** Estatísticas precisas da RPC (contagens por situação) */
  stats?: QueueStats;
  /** Callback para refresh manual */
  onRefresh: () => void;
}

export function QueueKanbanBoard({
  filters,
  stats,
  onRefresh,
}: QueueKanbanBoardProps) {
  // Estados para modal de chat
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  // Subscription Realtime UNIFICADA (1 canal por aba) — substitui as
  // antigas subscriptions por coluna que geravam dezenas de RPCs em pico.
  useServiceQueueRealtime();


  // Mapeia contagens de stats por situação para os badges das colunas
  const countsBySituation = useMemo(() => {
    if (!stats) return undefined;
    
    return {
      waiting_bot: stats.waiting_bot,
      waiting_agent: stats.waiting_agent,
      pending_transfer: stats.pending_transfers,
      in_service: stats.in_service,
      closed: stats.closed, // v3.1.0: Usar valor real da RPC
    };
  }, [stats]);

  // Abre modal de chat ao clicar na conversa
  const handleItemClick = useCallback((item: QueueItem) => {
    setSelectedConversationId(item.id);
    setIsChatModalOpen(true);
  }, []);

  // Fecha modal e atualiza board
  // Fecha modal (v3.3.0: NÃO chama onRefresh — o Realtime unificado
  // já reflete mudanças no card via patch otimista; invalidar tudo aqui
  // mascarava o realtime e disparava refetch desnecessário das colunas).
  const handleCloseModal = useCallback((open: boolean) => {
    setIsChatModalOpen(open);
    if (!open) {
      setSelectedConversationId(null);
    }
  }, []);

  // Conteúdo do Kanban - cada coluna busca seus próprios dados
  const kanbanContent = (
    <>
      <ScrollArea className="w-full">
        <div className="flex gap-4 px-4 pb-4 h-[calc(100vh-240px)]">
          {/* Cada coluna é independente e busca seus próprios dados */}
          {QUEUE_COLUMNS.map((column) => (
            <QueueKanbanColumn
              key={column.id}
              column={column}
              filters={filters}
              totalCount={countsBySituation?.[column.id as keyof typeof countsBySituation]}
              onItemClick={handleItemClick}
            />
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Modal de Chat */}
      <ChatConversationModal
        conversationId={selectedConversationId}
        open={isChatModalOpen}
        onOpenChange={handleCloseModal}
      />
    </>
  );

  // Renderiza com Sentry ErrorBoundary para captura automática de erros
  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <ErrorFallback error={error} resetError={resetError} />
        </div>
      )}
    >
      {kanbanContent}
    </SentryErrorBoundary>
  );
}
